import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { cropPhotoFiles } from '../utils/photoAlbumCropUpload.js'
import { installPhotoAlbumCropPanBounds } from '../utils/photoAlbumCropPanBounds.js'
import '../styles/AdminGalleryUpload.css'
import '../styles/MeriPhotoAlbum/PhotoAlbumCropUpload.css'
import '../styles/MeriPhotoAlbum/PhotoAlbumCropPanBounds.css'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''
const ADMIN_GALLERY_ALBUM_KEY = 'einv_admin_gallery_album'

installPhotoAlbumCropPanBounds()

function getApi(path) {
  return `${API_BASE_URL}${path}`
}

function readSelectedAlbumId() {
  const select = document.querySelector('#adm-gallery-album-select')
  return String(select?.value || localStorage.getItem(ADMIN_GALLERY_ALBUM_KEY) || '').trim()
}

function triggerGalleryRefresh() {
  const refreshButton = document.querySelector('.adm-gallery-page .adm-page-actions .adm-btn')
  refreshButton?.click()
}

function AdminGalleryUploadCard() {
  const inputRef = useRef(null)
  const [albumId, setAlbumId] = useState(() => readSelectedAlbumId())
  const [privacy, setPrivacy] = useState('public')
  const [busy, setBusy] = useState(false)
  const [status, setStatus] = useState({ type: '', message: '' })

  useEffect(() => {
    const select = document.querySelector('#adm-gallery-album-select')
    const syncAlbum = () => {
      setAlbumId(readSelectedAlbumId())
      setStatus({ type: '', message: '' })
    }

    syncAlbum()
    select?.addEventListener('change', syncAlbum)
    return () => select?.removeEventListener('change', syncAlbum)
  }, [])

  async function handleFiles(event) {
    const files = Array.from(event.target.files || [])
    event.target.value = ''
    if (!files.length || busy) return

    const currentAlbumId = readSelectedAlbumId()
    if (!currentAlbumId) {
      setStatus({ type: 'error', message: 'Select an E-Photo album before uploading.' })
      return
    }

    setBusy(true)
    setStatus({ type: 'progress', message: 'Prepare each selected photo in the crop window.' })

    try {
      const croppedFiles = await cropPhotoFiles(files)
      if (!croppedFiles.length) {
        setStatus({ type: '', message: '' })
        return
      }

      const formData = new FormData()
      formData.append('guestName', 'E-Invitation Admin')
      formData.append('notes', 'Uploaded from Gallery Management.')
      formData.append('photoPrivacy', JSON.stringify(croppedFiles.map(() => privacy)))
      croppedFiles.forEach((file) => formData.append('photos', file, file.name))

      setStatus({ type: 'progress', message: `Uploading ${croppedFiles.length} cropped photo${croppedFiles.length === 1 ? '' : 's'}…` })
      const response = await fetch(
        getApi(`/photo-albums/${encodeURIComponent(currentAlbumId)}/uploads`),
        { method: 'POST', body: formData }
      )
      const payload = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(payload.message || 'Failed to upload photos.')

      setStatus({
        type: 'success',
        message: `${croppedFiles.length} photo${croppedFiles.length === 1 ? '' : 's'} uploaded as ${privacy}.`,
      })
      triggerGalleryRefresh()
    } catch (error) {
      setStatus({ type: 'error', message: error?.message || 'Unable to crop or upload the selected photos.' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="adm-gallery-upload-card" aria-labelledby="adm-gallery-upload-title">
      <div className="adm-gallery-upload-copy">
        <p className="adm-page-eyebrow">Admin upload</p>
        <h2 id="adm-gallery-upload-title">Add photos to this album</h2>
        <p>
          Every selected image opens the same crop tool with Square, Portrait, and Landscape options before upload.
        </p>
      </div>

      <div className="adm-gallery-upload-controls">
        <label className="adm-gallery-upload-privacy">
          <span>Upload visibility</span>
          <select
            className="adm-input"
            value={privacy}
            onChange={(event) => setPrivacy(event.target.value)}
            disabled={busy}
          >
            <option value="public">Public</option>
            <option value="private">Private</option>
          </select>
        </label>

        <button
          type="button"
          className="adm-btn adm-btn-primary adm-gallery-upload-button"
          onClick={() => inputRef.current?.click()}
          disabled={!albumId || busy}
        >
          {busy ? 'Preparing photos…' : 'Choose & crop photos'}
        </button>

        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          hidden
          onChange={handleFiles}
        />
      </div>

      {status.message && (
        <p className={`adm-gallery-upload-status adm-gallery-upload-status-${status.type}`} role="status">
          {status.message}
        </p>
      )}
    </section>
  )
}

export default function AdminGalleryUploadPortal() {
  const [target, setTarget] = useState(null)

  useEffect(() => {
    let slot = null

    function attachSlot() {
      const selectorCard = document.querySelector('.adm-gallery-selector-card')
      if (!selectorCard) return

      const existing = document.querySelector('.adm-gallery-upload-slot')
      if (existing) {
        slot = existing
        setTarget(existing)
        return
      }

      slot = document.createElement('div')
      slot.className = 'adm-gallery-upload-slot'
      selectorCard.insertAdjacentElement('afterend', slot)
      setTarget(slot)
    }

    attachSlot()
    const observer = new MutationObserver(attachSlot)
    observer.observe(document.body, { childList: true, subtree: true })

    return () => {
      observer.disconnect()
      if (slot?.parentNode) slot.parentNode.removeChild(slot)
    }
  }, [])

  if (!target) return null
  return createPortal(<AdminGalleryUploadCard />, target)
}
